import type { Recording } from '../types/recorder'

const DB_NAME = 'recorder'
const STORE_NAME = 'recordings'

export function useIndexedDBRecording() {
  const initDb = (): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, 1)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result)

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true })
        }
      }
    })
  }

  const storeRecording = (db: IDBDatabase, recording: Omit<Recording, 'id' | 'queueId'>): Promise<IDBValidKey> => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.add(recording)

      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  const getStoredRecordings = (db: IDBDatabase): Promise<Recording[]> => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.getAll()

      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  const setRecordingsUploaded = (db: IDBDatabase, recordingId: number) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)

      const recordDetail = store.get(recordingId)

      recordDetail.onsuccess = () => {
        const recording: Recording = recordDetail.result as Recording
        const updatedRecording: Recording = { ...recording, isUploaded: true }
        const updateRequest: IDBRequest<IDBValidKey> = store.put(updatedRecording)

        updateRequest.onsuccess = () => resolve(updateRequest.result)
        updateRequest.onerror = () => reject(updateRequest.error)
      }

      recordDetail.onerror = () => reject(recordDetail.error)
    })
  }

  const removeUploadedRecording = (db: IDBDatabase) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.getAll()

      request.onsuccess = () => {
        const recordings = request.result

        const deletePromises = recordings
          .filter((recording: Recording) => recording.isUploaded)
          .map((recording: Recording) => removeRecording(db, recording.id))

        Promise.all(deletePromises)
          .then(() => resolve(true))
          .catch(reject)
      }

      request.onerror = () => reject(request.error)
    })
  }

  const removeRecording = (db: IDBDatabase, id: number) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.delete(id)

      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  const removeZeroSizeRecordings = (db: IDBDatabase) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      const request = store.getAll()

      request.onsuccess = () => {
        const recordings = request.result

        const deletePromises = recordings
          .filter((recording: Recording) => !recording.blob || recording.blob.size === 0)
          .map((recording: Recording) => removeRecording(db, recording.id))

        Promise.all(deletePromises)
          .then(() => resolve(true))
          .catch(reject)
      }

      request.onerror = () => reject(request.error)
    })
  }

  return {
    initDb,
    storeRecording,
    getStoredRecordings,
    removeRecording,
    setRecordingsUploaded,
    removeUploadedRecording,
    removeZeroSizeRecordings,
  }
}
